/**
 * Manages child windows including
 *
 * <ul>
 *     <li>Opening
 *     <li>Passing initial data
 *     <li>Notifying user in case open is blocked
 *     <li>Executing a callback when a child closes
 *     <li>Closing children when the parent window closes
 * </ul>
 *
 * @module MW/uiframework/uicontainer/ChildWindowManager
 * @copyright Copyright 2018-2020 The MathWorks, Inc.
 */
define(["dojo/_base/lang",
        "dojo/Deferred",
        "dojo/on",
        "mw-notifications/Notifications",
        "mw-utils/Utils",
        "MW/uiframework/uicontainer/WindowUtils",
        "dojo/i18n!l10n/nls/dialog",
        "dojo/i18n!l10n/nls/uiframework"
], function (lang, Deferred, on, Notifications, Utils, WindowUtils, dialogL10n, uiframeworkL10n) {

    // Save in window so that child windows can reference
    window.ChildWindowManager = {

        _childInfo: {},  // Keyed by window name, stores object with:
        //   id
        //   initialData
        //   closeHandler
        //   poller
        //   window

        _childBounds: {}, // Keyed by id, stores object with x, y, w and h.

        /**
         * Opens a child window
         *
         * @param options {Object} an object containing any of the following options:
         * @param options.page {string} the page to load in the child window.  The page is presumed to have the same window origin as the parent
         * @param options.url {string} the URL to load in the child window.
         * @param options.childTitle the title that will be displayed on the child window.  The ChildWindowManager does not take
         *                           any steps to cause this title to appear on the window.  Rather it is used if there is need to
         *                           communicate with the user about the window.  For example, if the open is blocked by a browser.
         * @param options.id {string} identifies the child window.  An id is optional but is required if bounds persistence is desired.
         * @param options.tabbed {boolean} true if child should open in a browser tab rather than a separate browser window.
         *                                 This option is ignored when running in CEF rather than a browser.
         * @param options.initialData {Object} data to be
         * @param options.bounds {x: number, y: number, w: number, h: number}
         * @param options.persistBounds {boolean} true if the child bounds should be remembered from one open to the next (requires an id)
         * @param options.closeHandler {function}
         * @param optons.singleDocument {boolean} true if the default size (used when no bounds specified and no bound history)
         *                                        should be choosen to accommodate a single document
         * @returns {Promise} A promise that resolves to a child window if the open is successful
         */
        open: function(options) {
            var that = this;
            var bounds;
            var deferred = new Deferred();
            if (options.persistBounds && options.id) {
                bounds = this._childBounds[options.id];
                if (bounds || !this._sessionDataService) {
                    this._doOpen(options, bounds, deferred);
                } else {
                    this._sessionDataService.retrieve("childWidowBounds" + options.id).then(
                        function (retrievedBounds) {
                            that._doOpen(options, retrievedBounds, deferred);
                        },
                        function () {
                            that._doOpen(options, bounds, deferred);
                        }
                    )
                }
            } else {
                this._doOpen(options, bounds, deferred);
            }
            return deferred.promise;
        },

        /**
         * Similar to open but runs synchronously.  This allows the method to return the child window
         * directly (instead of a promise) but in doing so sacrifices the opportunity to allow the user
         * to override popup-blocking.  Opening synchronously also forgoes the possibility of recovering
         * and applying the window bounds from a previous session.
         *
         * @deprecated Use the open method whenever possible.
         */
        openSynchronously: function(options) {
            var bounds;
            if (options.persistBounds && options.id) {
                bounds = this._childBounds[options.id];
            }
            var childInfo = this._preOpen(options, bounds);
            var child =  options.tabbed ? window.open(childInfo.url, childInfo.name) :
                window.open(childInfo.url, childInfo.name, "location=no,menubar=no,toolbar=no,status=no," + formatBounds(childInfo.initialBounds));
            if (child) {
                this._postOpen(child, options, childInfo);
            } else {
                this._reportOpenFailure();
                delete this._childInfo[childInfo.name];
            }
            return child;
        },

        _doOpen: function(options, bounds, deferred) {
            var childInfo = this._preOpen(options, bounds);
            this._tryOpen(options, childInfo, deferred);
        },

        _preOpen: function(options, bounds) {
            // Define/refine the bounds as needed
            if (!bounds) {
                bounds = options.bounds || {x: -1, y: -1, w: -1, h: -1};
            }
            WindowUtils.applyDefaultBounds(bounds, options.singleDocument);
            WindowUtils.convertBoundsToPixels(bounds);
            if (window.cefclient) {
                bounds = WindowUtils.convertOrigin(window, bounds);
            }

            // Pick a random name for the child window.
            var name = "_" + Utils.generateUuid();

            // Save information about the child under its name
            var childInfo = {
                id: options.id,
                initialData: options.initialData,
                closeHandler: options.closeHandler,
                name: name,
                url: options.url || new URL(options.page, window.location.href),
                initialBounds: bounds
            };
            this._childInfo[name] = childInfo;

            return childInfo;
        },

        _tryOpen: function(options, childInfo, deferred) {
            var that = this;
            var child =  options.tabbed ? window.open(childInfo.url, childInfo.name) :
                         window.open(childInfo.url, childInfo.name, "location=no,menubar=no,toolbar=no,status=no," + formatBounds(childInfo.initialBounds));
            if (child) {
                // Open succeeded
                this._postOpen(child, options, childInfo);
                deferred.resolve(child);
            } else if (window.cefclient) {
                // Open failed and since we're running in CEF there is no recourse
                this._reportOpenFailure();
                delete this._childInfo[childInfo.name];
                deferred.reject("open failed");
            } else {
                // The browser prevented child window open.  Presumably this is due to pop-up blocking
                // If the open is retried in response to a user click it should work.  To that end
                // open a dialog.
                var message;
                if (options.childTitle) {
                    message = lang.replace(uiframeworkL10n.titledOpenBlockedMessage, {placeholder: options.childTitle});
                } else {
                    message = uiframeworkL10n.openBlockedMessage;
                }
                message += "\n" + uiframeworkL10n.openPermissionRequest + "\n\n" +
                                  uiframeworkL10n.allowPopupsInstruction;
                Notifications.displayConfirmDialog(uiframeworkL10n.windowOpenTitle, message, {
                    icon: "question",
                    modal: true,
                    buttonText: [uiframeworkL10n.open, dialogL10n.cancel],
                    closeCallback: function(e) {
                        if (e.response === 1) {
                            that._tryOpen(options, childInfo, deferred);
                        } else {
                            // The user chose not to allow the child open
                            delete that._childInfo[childInfo.name];
                            deferred.reject("open permission denied");
                        }
                    }
                });
            }
        },

        _postOpen: function(child, options, childInfo) {
            var that = this;
            if (!window.cefclient) {
                // The position and size features passed to the open function about don't seem to be honored in the browser
                WindowUtils.setWindowBounds(child, childInfo.initialBounds);
            } else {
                // In the CEF case the size features are honored but the window resize handler doesn't fire without
                // doing the following.  The timeout allows for a window bounds listener to be put in place by the caller.
                setTimeout(function() {
                    WindowUtils.setWindowBounds(child, childInfo.initialBounds);
                }, 0);
            }

            // Save a reference to the original opener.
            // Handles to windows can be obtained by name with window.open()
            // but as a side effect the opener field changes. This causes
            // issues with cross-domain operations. Use child.originalOpener
            // instead of child.opener wherever applicable.
            child.originalOpener = child.opener;

            //need this here because if child.closed, child.name becomes ""
            var childName = child.name;

            // Listen for child window close
            on(child, "beforeunload", function () {

                //make sure this is indeed a close, not a refresh
                setTimeout(function() {
                    if(child.closed) {
                        that._childClosed(child, childName);
                    }
                },500);
            });

            // Poll in case the spawned window is closed before it has a chance to complete initialization,
            // in which case the beforeunload event doesn't fire.
            childInfo.poller = setInterval(function () {
                if (child.closed) {
                    that._childClosed(child, childName); 
                }
            }, 1000);

            if (options.id) {
                // Listen for and remember window bounds changes
                WindowUtils.addWindowBoundsListener(child, function(bounds) {
                    that._childBounds[options.id] = bounds;
                    var sessionDataService = child.originalOpener.ChildWindowManager._sessionDataService;
                    if (sessionDataService) {
                        sessionDataService.save("childWidowBounds" + options.id, bounds);
                    }
                });
            }
        },

        /**
         * Gets the initial data for a child window.  This method is expected to be called in the context of the child window.
         *
         * @param discard {boolean} true if the data should be discarded.  If the data is discarded, subsequent calls to
         *        this method will return falsey.
         * @returns the initial data
         */
        getInitialData: function(discard) {
            var data = this._getChildInfo().initialData;
            if (discard && data) {
                this._discardInitialData();
            }
            return data;
        },

        /**
         * Informs the parent that a child window has successfully opened.  This should be called by the child window,
         * typically after it has initialized its content.
         */
        opened: function () {
            // Delay response because there seems to be a time period between when the child has loaded and
            // when we can count on the beforeunload event firing in response to close.
            var that = this;
            setTimeout(function() {
                that._cancelPolling();
            }, 2000);

        },

        bringToFront: function (childId) {
            var childWindow = this.getWindowById(childId);
            if (childWindow) {
                WindowUtils.bringToFront(childWindow);
            }
        },

        /**
         * Sets the session data service to be used for persisting child window bounds
         *
         * @param service {SessionDataService}
         */
        setSessionDataService: function(service) {
            this._sessionDataService = service;
        },

        /**
         * Closes all child windows
         */
        closeChildren: function () {
            var that = this;
            Object.keys(this._childInfo).forEach(function (childName) {
                var info = that._childInfo[childName];
                if (info.window) {
                    info.window.close();
                    delete info.window;
                }
            });
            this._childInfo = {};
        },

        _reportOpenFailure: function () {
            var message;
            if (window.cefclient) {
                message = uiframeworkL10n.unableToOpenMessage;
            } else {
                message = uiframeworkL10n.openBlockedMessage;
                message += "\n\n" + uiframeworkL10n.allowPopupsInstruction;
            }
            Notifications.displayAlertDialog(uiframeworkL10n.windowOpenTitle, message, {
                icon: 'warning',
                modal: true
            });
        },

        // Former name of _reportOpenFailure. Preserved because mw-webwindow reaches in and uses.
        _handleOpenFailure: function () {
            this._reportOpenFailure;
        },

        _getChildInfo: function() {
            // Called in context of child window
            return window.opener.ChildWindowManager._childInfo[window.name];
        },

        _discardInitialData: function () {
            // Called in context of child window
            var childInfo = this._getChildInfo();
            if (childInfo) {
                delete childInfo.initialData;
            }
        },

        _cancelPolling: function () {
            // Runs in context of child window
            var childInfo = this._getChildInfo();
            if (childInfo && childInfo.poller) {
                window.opener.clearInterval(childInfo.poller);
                delete childInfo.poller;
            }
        },

        _childClosed: function(child, childName) {
            // Runs in context of parent window
            var childInfo = this._childInfo[childName];

            //if the poller hasn't already cleaned up
            if (childInfo) {
                if (childInfo.poller) {
                    clearInterval(childInfo.poller);
                }
                delete this._childInfo[childName];

                // Notify close handler
                if (childInfo.closeHandler) {
                    childInfo.closeHandler({id: childInfo.id, childWindow: child});
                }
            }
        },

        getWindowByName: function (name) {
            var info = this._childInfo[name];
            return info ? info.window : null;
        },

        getWindowById: function (id) {
            var that = this;
            var names = Object.keys(this._childInfo);
            for (var i = 0; i < names.length; i++) {
                var info = that._childInfo[names[i]];
                if (info.id === id) {
                    return info.window;
                }
            }
            return null;
        },

    };

    var formatBounds = function(bounds) {
        return "left=" + bounds.x + ",top=" + bounds.y + ",width=" + bounds.w + ",height=" + bounds.h;
    };

    return window.ChildWindowManager;
});